import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type FormEvent } from "react";
import type { ApprovalTemplate } from "../api/generated";
import { isApiClientError, isValidationApiError } from "../api/errors";
import type {
  ApprovalTemplateInput,
  ApprovalTemplatePatch,
} from "../entities/document-workflow/approvals";
import { ApprovalLevelsEditor } from "./ApprovalLevelsEditor";
import {
  approvalLevelErrors,
  approvalLevelInputs,
  newApprovalLevel,
  type ApprovalLevelDraft,
} from "./approvalsSupport";
import { shellBootstrapQuery } from "./bootstrap";
import { TextField } from "./CounterpartyFields";
import { useSubmissionKey } from "./counterpartiesSupport";
import { Notice } from "./Notice";
import { Select } from "./Select";
import type { ShellServices } from "./services";

import {
  approvalTemplateKeys,
  approvalTemplateError,
} from "./approvalsSupport";

export function ApprovalTemplateForm({
  template,
  services,
  onSaved,
  onCancel,
}: {
  template?: ApprovalTemplate;
  services: ShellServices;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const bootstrap = useQuery(shellBootstrapQuery(services)).data;
  const client = useQueryClient();
  const key = useSubmissionKey();
  const sending = useRef(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [scopeId, setScopeId] = useState(
    template?.access_scope.id ?? bootstrap?.currentScope?.id ?? "",
  );
  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [type, setType] = useState<
    NonNullable<ApprovalTemplate["document_type"]> | ""
  >(template?.document_type ?? "");
  const [kind, setKind] = useState<
    NonNullable<ApprovalTemplate["internal_kind"]> | ""
  >(template?.internal_kind ?? "");
  const [active, setActive] = useState(template?.is_active ?? true);
  const [levels, setLevels] = useState<ApprovalLevelDraft[]>(
    () =>
      template?.steps.map((step) => ({
        key: crypto.randomUUID(),
        mode: step.mode,
        amount: step.due?.amount.toString() ?? "",
        unit: step.due?.unit ?? "working_days",
        approver_ids: step.approvers.map((person) => person.id),
      })) ?? [newApprovalLevel()],
  );
  const users = useQuery({
    queryKey: [...approvalTemplateKeys, "people", scopeId],
    enabled: scopeId !== "",
    retry: false,
    queryFn: () => services.approvals!.templateCandidates(scopeId),
  });
  const people = new Map<
    string,
    {
      id: string;
      display_name: string;
      substituted_by?: { id: string; display_name: string } | null;
    }
  >(
    (users.data ?? []).map((person) => [
      person.id,
      {
        id: person.id,
        display_name: person.display_name,
        substituted_by: person.substituted_by,
      },
    ]),
  );
  // Сохранённые согласующие остаются видны при правке, даже если теперь отсутствуют.
  for (const step of template?.steps ?? [])
    for (const person of step.approvers)
      if (!people.has(person.id)) people.set(person.id, person);

  function changed() {
    key.reset();
    setError(null);
    setErrors({});
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (sending.current) return;
    const invalid = approvalLevelErrors(levels);
    if (name.trim() === "") invalid.name = "Укажите название";
    if (name.trim().length > 150) invalid.name = "Максимум 150 символов";
    if (scopeId === "") invalid.access_scope_id = "Выберите организацию";
    setErrors(invalid);
    if (Object.keys(invalid).length > 0) return;
    const body: ApprovalTemplatePatch = {
      name: name.trim(),
      description: description.trim() || null,
      document_type: type || null,
      internal_kind: type === "internal" ? kind || null : null,
      is_active: active,
      steps: approvalLevelInputs(levels),
    };
    sending.current = true;
    setPending(true);
    setError(null);
    try {
      const api = services.approvals!;
      const saved =
        template === undefined
          ? await api.createTemplate(
              {
                ...body,
                access_scope_id: scopeId,
              } satisfies ApprovalTemplateInput,
              key.current(),
            )
          : await api.updateTemplate(template.id, body, key.current());
      // Сначала снимаем отправку, затем обновляем данные и закрываем форму.
      setPending(false);
      key.reset();
      client.setQueryData([...approvalTemplateKeys, "detail", saved.id], saved);
      await client.invalidateQueries({ queryKey: approvalTemplateKeys });
      onSaved();
    } catch (failure) {
      setPending(false);
      setError(failure);
      if (isValidationApiError(failure)) {
        setErrors(
          Object.fromEntries(
            Object.entries(failure.details.fields).map(([field, messages]) => [
              field,
              messages[0] ?? "Проверьте поле",
            ]),
          ),
        );
      }
    } finally {
      sending.current = false;
    }
  }
  return (
    <form
      className="cp-card approval-form"
      noValidate
      onSubmit={(event) => void submit(event)}
      onChange={changed}
    >
      <h2>{template === undefined ? "Создать шаблон" : "Изменить шаблон"}</h2>
      {error !== null &&
      !(
        isApiClientError(error) &&
        error.code === "document_workflow.approval_steps_invalid"
      ) ? (
        <Notice>{approvalTemplateError(error)}</Notice>
      ) : null}
      <fieldset disabled={pending}>
        {template ? (
          <p>Организация: {template.access_scope.name}</p>
        ) : (
          <label className="field">
            <span>Организация</span>
            <Select
              value={scopeId}
              onChange={(event) => {
                setScopeId(event.target.value);
                setLevels((current) =>
                  current.map((level) => ({ ...level, approver_ids: [] })),
                );
              }}
            >
              <option value="">Выберите организацию</option>
              {bootstrap?.scopes.map((scope) => (
                <option key={scope.id} value={scope.id}>
                  {scope.name}
                </option>
              ))}
            </Select>
          </label>
        )}
        {errors.access_scope_id ? (
          <small className="field-error" role="alert">
            {errors.access_scope_id}
          </small>
        ) : null}
        <TextField
          label="Название"
          value={name}
          onChange={setName}
          maxLength={150}
          error={errors.name}
        />
        <TextField
          label="Описание"
          value={description}
          onChange={setDescription}
          maxLength={10000}
          multiline
          error={errors.description}
        />
        <label className="field">
          <span>Применяется к</span>
          <Select
            value={type}
            onChange={(event) => {
              const value = event.target.value;
              setType(
                value === "incoming" ||
                  value === "outgoing" ||
                  value === "internal"
                  ? value
                  : "",
              );
              setKind("");
            }}
          >
            <option value="">Любой документ</option>
            <option value="incoming">Входящий</option>
            <option value="outgoing">Исходящий</option>
            <option value="internal">Внутренний</option>
          </Select>
        </label>
        {type === "internal" ? (
          <label className="field">
            <span>Вид внутреннего документа</span>
            <Select
              value={kind}
              onChange={(event) =>
                setKind(
                  event.target.value === "order"
                    ? "order"
                    : event.target.value === "memo"
                      ? "memo"
                      : "",
                )
              }
            >
              <option value="">Любой</option>
              <option value="order">Приказ</option>
              <option value="memo">Служебная записка</option>
            </Select>
          </label>
        ) : null}
        <label className="cp-check">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
          <span>Включён</span>
        </label>
        {users.isLoading ? (
          <p aria-busy="true">Загружаем сотрудников…</p>
        ) : null}
        {users.isError ? (
          <Notice>
            Не удалось загрузить сотрудников.{" "}
            <button type="button" onClick={() => void users.refetch()}>
              Повторить
            </button>
          </Notice>
        ) : null}
        <ApprovalLevelsEditor
          levels={levels}
          people={[...people.values()]}
          disabled={pending || scopeId === ""}
          errors={{
            ...errors,
            ...(isApiClientError(error) &&
            error.code === "document_workflow.approval_steps_invalid"
              ? { steps: approvalTemplateError(error) }
              : {}),
          }}
          onChange={(next) => {
            changed();
            setLevels(next);
          }}
        />
        <div className="approval-actions">
          <button
            className="button button--primary"
            type="submit"
            disabled={pending || !scopeId || users.isLoading || users.isError}
          >
            Сохранить
          </button>
          <button
            className="button button--secondary"
            type="button"
            onClick={onCancel}
          >
            Отмена
          </button>
        </div>
      </fieldset>
    </form>
  );
}
